/**
 * Reusable React Authentication & Security Hooks powered by Supabase Auth.
 *
 * Provides real-time reactive session state for login, phone OTP, registration,
 * and secure sign out, automatically syncing Supabase session updates across the app.
 */

import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { Session, User } from "@supabase/supabase-js";
import type { UserPermission, ExtendedUser } from "@/types/auth";
import { isAdminRole, normalizeUserRole, type UserRole } from "@/lib/roles";

export type { ExtendedUser };

export function formatSupabaseUser(user: User | null, profileRole?: string): ExtendedUser | null {
  if (!user) return null;

  const metadata = user.user_metadata || {};
  const appMetadata = user.app_metadata || {};
  const name =
    metadata.full_name ||
    metadata.name ||
    (user.email ? user.email.split("@")[0] : undefined) ||
    (user.phone ? `User ${user.phone.slice(-4)}` : "Valued Customer");

  // Normalised through the shared vocabulary rather than compared against the literal
  // string "ADMIN". The previous line was
  //   const role: UserRole = rawRole === "ADMIN" ? "ADMIN" : "CUSTOMER";
  // which turned SUPER_ADMIN, STORE_MANAGER and STAFF into CUSTOMER — so a staff account
  // the database treats as an administrator was shown the 403 screen. See src/lib/roles.ts.
  const role: UserRole = normalizeUserRole((appMetadata.role as string) || profileRole);

  return {
    id: user.id,
    email: user.email || undefined,
    phone: user.phone || undefined,
    name,
    role,
    permissions: (appMetadata.permissions as UserPermission[]) || [],
    createdAt: user.created_at,
  };
}

export function useCurrentUser() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadUserWithRole(authUser: User | null): Promise<ExtendedUser | null> {
      if (!authUser) return null;

      const appMetadataRole = (authUser.app_metadata?.role as string) || "";
      if (isAdminRole(appMetadataRole)) {
        return formatSupabaseUser(authUser, appMetadataRole);
      }

      let profileRole: string | undefined = undefined;
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", authUser.id)
          .maybeSingle();
        if (!error && data?.role) {
          profileRole = data.role;
        }
      } catch {
        // Fall back to metadata or admin_users
      }

      // Three separate `.eq()` lookups rather than one interpolated `.or()` filter.
      // The `.or()` grammar takes raw text, so a value containing filter syntax would
      // have appended its own conditions — the same class of bug removed from
      // api/verify-razorpay-payment.ts. `.eq()` sends its argument as a single
      // percent-encoded parameter value.
      if (!isAdminRole(profileRole) && authUser.email) {
        const adminUserCriteria: [string, string][] = [
          ["user_id", authUser.id],
          ["email", authUser.email],
        ];
        for (const [column, value] of adminUserCriteria) {
          try {
            const { data } = await supabase
              .from("admin_users")
              .select("role")
              .eq(column, value)
              .maybeSingle();
            if (data?.role) {
              profileRole = data.role;
              break;
            }
          } catch {
            // Ignore and try the next criterion
          }
        }
      }

      return formatSupabaseUser(authUser, profileRole);
    }

    async function initAuth() {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (mounted) {
          setSession(data.session);
          const extended = await loadUserWithRole(data.session?.user ?? null);
          if (mounted) setUser(extended);
        }
      } catch (err: unknown) {
        if (mounted) setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, currentSession) => {
      if (mounted) {
        setSession(currentSession);
        const extended = await loadUserWithRole(currentSession?.user ?? null);
        if (mounted) {
          setUser(extended);
          setIsLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    setIsLoading(true);
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setIsLoading(false);
  };

  const isAuthenticated = Boolean(user);
  const role: UserRole = user?.role ?? "CUSTOMER";
  // The shared vocabulary decides, not a literal comparison. `role === "ADMIN"` turned
  // SUPER_ADMIN and STORE_MANAGER — both of which the database's is_admin() honours —
  // into `false` here, and every admin gate in the app reads this flag, so those accounts
  // still saw the 403 screen after the role strings themselves were normalised.
  const isAdmin = isAdminRole(role);
  const permissions: UserPermission[] = user?.permissions ?? [];

  return {
    user,
    session,
    isAuthenticated,
    isAdmin,
    role,
    permissions,
    isLoading,
    error,
    signOut,
  };
}

/**
 * React UX Guard — Checks if current user has required role(s).
 */
export function useRequireRole(allowedRoles: UserRole | UserRole[]) {
  const { role, isAuthenticated, isLoading } = useCurrentUser();
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  const hasRole = isAuthenticated && roles.includes(role);

  return {
    hasRole,
    isLoading,
    role,
  };
}

/**
 * React UX Guard — Checks if current user has required permission(s).
 */
export function useRequirePermission(requiredPermissions: UserPermission | UserPermission[]) {
  const { permissions, isAdmin, isAuthenticated, isLoading } = useCurrentUser();
  const needed = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

  const hasPermission =
    isAuthenticated && (isAdmin || needed.every((p) => permissions.includes(p)));

  return {
    hasPermission,
    isLoading,
    permissions,
  };
}
